import type { Rect } from './geometry';
import type { LayerId } from './model';

export interface DamagedTile {
  layerId: LayerId;
  x: number;
  y: number;
  target: 'pixels' | 'mask';
}

/** A compact, serializable description of pixels and composites invalidated by an edit. */
export interface DamageSet {
  layerIds: readonly LayerId[];
  tiles: readonly DamagedTile[];
  bounds: readonly Rect[];
  structural: boolean;
  fullDocument: boolean;
}

export const EMPTY_DAMAGE: DamageSet = Object.freeze({
  layerIds: Object.freeze([]),
  tiles: Object.freeze([]),
  bounds: Object.freeze([]),
  structural: false,
  fullDocument: false,
});

export function damage(options: Partial<DamageSet> = {}): DamageSet {
  const tiles = [...new Map((options.tiles ?? []).map((tile) => [`${tile.layerId}:${tile.target}:${tile.x}:${tile.y}`, { ...tile }])).values()];
  const validBounds = (options.bounds ?? []).filter((bounds) => [bounds.x, bounds.y, bounds.width, bounds.height].every(Number.isFinite) && bounds.width >= 0 && bounds.height >= 0);
  const bounds = validBounds.length === 0 ? [] : [{
    x: Math.min(...validBounds.map((rect) => rect.x)),
    y: Math.min(...validBounds.map((rect) => rect.y)),
    width: Math.max(...validBounds.map((rect) => rect.x + rect.width)) - Math.min(...validBounds.map((rect) => rect.x)),
    height: Math.max(...validBounds.map((rect) => rect.y + rect.height)) - Math.min(...validBounds.map((rect) => rect.y)),
  }];
  return {
    layerIds: [...new Set(options.layerIds ?? [])],
    tiles,
    bounds,
    structural: options.structural ?? false,
    fullDocument: options.fullDocument ?? false,
  };
}

export function mergeDamage(...sets: readonly DamageSet[]): DamageSet {
  return damage({
    layerIds: sets.flatMap((set) => set.layerIds),
    tiles: sets.flatMap((set) => set.tiles),
    bounds: sets.flatMap((set) => set.bounds),
    structural: sets.some((set) => set.structural),
    fullDocument: sets.some((set) => set.fullDocument),
  });
}
